import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useScheduleCall } from "../context/ScheduleCallContext";
import {
  TrendingUp,
  Calendar,
  Phone,
  Users,
  RefreshCw,
  Code2,
} from "lucide-react";
import TiltCard from "./TiltCard";

const iconProps = { className: "w-7 h-7", strokeWidth: 1.75 };

const services = [
  {
    title: "Lead Generation Websites",
    description:
      "Conversion-optimized websites designed to capture, qualify, and deliver high-intent leads directly to your inbox or CRM.",
    features: [
      "Landing page funnels",
      "Form & chat capture",
      "SEO optimization",
      "Analytics integration",
    ],
    icon: <TrendingUp {...iconProps} />,
    glow: "primary",
    gradient: "from-primary-600/20 to-primary-400/5",
  },
  {
    title: "Booking & Reservation Platforms",
    description:
      "End-to-end booking systems with real-time availability, payments, and admin dashboards — perfect for homestays, hotels, and service businesses.",
    features: [
      "Real-time availability",
      "Payment processing",
      "Admin dashboard",
      "Mobile responsive",
    ],
    icon: <Calendar {...iconProps} />,
    glow: "cyan",
    gradient: "from-accent-cyan/20 to-accent-cyan/5",
  },
  {
    title: "AI Inbound Call Agents",
    description:
      "AI-powered voice agents that answer inbound calls 24/7, qualify leads, answer FAQs, and book appointments — never miss a potential customer again.",
    features: [
      "24/7 call handling",
      "Lead qualification",
      "Appointment booking",
      "CRM integration",
    ],
    icon: <Phone {...iconProps} />,
    glow: "violet",
    gradient: "from-accent-violet/20 to-accent-violet/5",
  },
  {
    title: "Custom CRM Systems",
    description:
      "Tailored CRM platforms that match your exact workflow — manage leads, bookings, customers, and operations from one powerful dashboard.",
    features: [
      "Custom workflows",
      "Lead pipeline",
      "Reporting & analytics",
      "Team management",
    ],
    icon: <Users {...iconProps} />,
    glow: "emerald",
    gradient: "from-accent-emerald/20 to-accent-emerald/5",
  },
  {
    title: "Business Automation",
    description:
      "Automate repetitive tasks — email sequences, data sync, notifications, and integrations that save hours and eliminate human error.",
    features: [
      "Workflow automation",
      "API integrations",
      "Email sequences",
      "Data synchronization",
    ],
    icon: <RefreshCw {...iconProps} />,
    glow: "rose",
    gradient: "from-accent-rose/20 to-accent-rose/5",
  },
  {
    title: "Full-Stack Web Applications",
    description:
      "Scalable, production-grade web applications built with modern tech — from MVPs to enterprise systems that grow with your business.",
    features: [
      "React / Node.js stack",
      "Cloud deployment",
      "Database design",
      "Performance optimized",
    ],
    icon: <Code2 {...iconProps} />,
    glow: "primary",
    gradient: "from-primary-600/20 to-accent-cyan/5",
  },
];

const Services = () => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const { openScheduleCall } = useScheduleCall();

  return (
    <section id="services" className="section-padding bg-dark-300 relative overflow-hidden">
      <div className="absolute inset-0 bg-mesh-gradient opacity-50 pointer-events-none" />

      <div className="section-container relative z-10">
        <motion.div
          ref={ref}
          className="text-center mb-16 md:mb-20"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="text-primary-400 font-semibold text-sm uppercase tracking-widest">
            What We Build
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5 gradient-text">
            Services That Drive Revenue
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Every product we build is designed with one goal: turn your digital
            presence into a money-making machine for your business.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + index * 0.08, duration: 0.6 }}
            >
              <TiltCard glowColor={service.glow} className="h-full">
                <div
                  className={`glass-panel-hover h-full p-7 bg-gradient-to-br ${service.gradient} group`}
                >
                  <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-primary-400 mb-5 group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>

                  <h3 className="font-display text-xl font-bold text-white mb-3 group-hover:text-primary-300 transition-colors">
                    {service.title}
                  </h3>

                  <p className="text-gray-400 text-sm leading-relaxed mb-5">
                    {service.description}
                  </p>

                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2 text-sm text-gray-500"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </TiltCard>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
        >
          <p className="text-gray-400 mb-6 text-lg">
            Not sure which service fits? Let's talk — free 30-minute consultation.
          </p>
          <motion.button
            onClick={openScheduleCall}
            className="btn-primary"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Book Free Consultation
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
